import React from "react";
import { MetricCard } from "../Components/MetricCard";
import "../styles.css";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useEffect } from "react";
import { useState } from "react";

export default function Dashboard({...props}) {
  
  const [growthData, setGrowthData] = useState([]);
  const [tierData, setTierData] = useState([]);
  const [newThisWeek, setNewThisWeek] = useState(0);
  const [newThisMonth, setNewThisMonth] = useState(0);
  const [conversionRate, setConversionRate] = useState("0%");
  useEffect(() => {
    if(props.stripeStats) {
      const tierDataArray = Object.entries(props.stripeStats.activeSubscriptionsByTier || {}).map(([name, value]) => ({
        name,
        value,
        color: name === "Free Plan" ? "#94a3b8" : 
               name === "Basic" ? "#635bff" : 
               name === "Pro" ? "#7c73ff" : 
               "#a39dff"
      }));
      const growthDataArray = formatChartData(props.stripeStats);
      const newThisWeekCount = props.stripeStats.newSignups.filter(event => {
        const eventDate = new Date(event.occurredAt * 1000);
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return eventDate >= sevenDaysAgo && eventDate <= now;
      }).length;
      setNewThisWeek(newThisWeekCount);
      const newThisMonthCount = props.stripeStats.newSignups.filter(event => {
        const eventDate = new Date(event.occurredAt * 1000);
        const now = new Date();
        const thirtyDaysAgo = new Date(now).setMonth(new Date(now).getMonth() - 1);
        return eventDate >= thirtyDaysAgo && eventDate <= now;
      }).length;
      setNewThisMonth(newThisMonthCount);
      const conversionRateValue = props.stripeStats.newSignupsCount > 0 ? 
        ((props.stripeStats.paidUpgradesCount / props.stripeStats.newSignupsCount) * 100).toFixed(2) : 0;
      setConversionRate(`${conversionRateValue}%`);
      setGrowthData(growthDataArray);
      setTierData(tierDataArray);
    }
  },[props.stripeStats])

  const formatChartData = (response) => {
  const { newSignups = [], paidUpgrades = [] } = response;
  const dateMap = {};

  // Helper to format Unix timestamp (seconds) to "MMM D" (e.g., Jan 28)
  const formatDate = (seconds) => {
    return new Date(seconds * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // 1. Process New Signups
  newSignups.forEach(event => {
    const dateKey = formatDate(event.occurredAt);
    if (!dateMap[dateKey]) {
      dateMap[dateKey] = { date: dateKey, newSignups: 0, paidUpgrades: 0, rawDate: event.occurredAt };
    }
    dateMap[dateKey].newSignups += 1;
  });

  // 2. Process Paid Upgrades
  paidUpgrades.forEach(event => {
    const dateKey = formatDate(event.occurredAt);
    if (!dateMap[dateKey]) {
      dateMap[dateKey] = { date: dateKey, newSignups: 0, paidUpgrades: 0, rawDate: event.occurredAt };
    }
    dateMap[dateKey].paidUpgrades += 1;
  });

  // 3. Convert Map to Array and Sort Chronologically
  return Object.values(dateMap).sort((a, b) => a.rawDate - b.rawDate);
};

  return (
    <div className="dash">
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="dashboard-header-content">
            <div className="dashboard-header-left">
              <div className="dashboard-title-section">
                <h1>Dashboard</h1>
                <p>Monitor your subscription metrics and growth</p>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="dashboard-content">
          {/* KPI Cards */}
          <div className="kpi-grid">
            <MetricCard
              label="New This Week"
              value={newThisWeek ? newThisWeek : "Loading..."}
              change="+12%"
              changeType="positive"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                  <polyline points="16 7 22 7 22 13" />
                </svg>
              }
            />
            <MetricCard
              label="New This Month"
              value={newThisMonth ? newThisMonth : "Loading..."}
              change="+24%"
              changeType="positive"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              }
            />
            <MetricCard
              label="Conversion Rate"
              value={conversionRate ? conversionRate : "Loading..."}
              change="+3.1%"
              changeType="positive"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
              }
            />
            <MetricCard
              label="Active Subscriptions"
              value={props.stripeStats ? props.stripeStats.activeSubscriptionsTotal : "Loading..."}
              change="+8%"
              changeType="positive"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              }
            />
          </div>

          {/* Charts Section */}
          <div className="charts-section">
            {/* Growth Chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Subscription Growth</h3>
                <p>New signups vs. paid upgrades (Last 30 days)</p>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={growthData}>
                  <defs>
                    <linearGradient
                      id="colorSignups"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      {/* <stop offset="5%" stopColor="#635bff" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#635bff" stopOpacity={0} /> */}
                    </linearGradient>
                    <linearGradient
                      id="colorUpgrades"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      {/* <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} /> */}
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "#64748b" }}
                  />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Area
                    type="monotone"
                    dataKey="newSignups"
                    stroke="#635bff"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorSignups)"
                    name="New Signups"
                  />
                  <Area
                    type="monotone"
                    dataKey="paidUpgrades"
                    stroke="#10b981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorUpgrades)"
                    name="Paid Upgrades"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Tier Breakdown Donut Chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Tier Distribution</h3>
                <p>Current user breakdown</p>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={tierData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {tierData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="tier-legend">
                {tierData.map((tier) => (
                  <div key={tier.name} className="tier-legend-item">
                    <div className="tier-legend-label">
                      <div
                        className="tier-legend-dot"
                        style={{ backgroundColor: tier.color }}
                      ></div>
                      <span className="tier-legend-name">{tier.name}</span>
                    </div>
                    <span className="tier-legend-value">{tier.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* <div className="activity-section">
            <div className="activity-table-container">
              <div className="activity-table-header">
                <h3>Recent Activity</h3>
                <p>Latest subscription events</p>
              </div>
              <div>
                <table className="activity-table">
                  <thead>
                    <tr>
                      <th>
                        <span>User Email</span>
                      </th>
                      <th>
                        <span>Plan</span>
                      </th>
                      <th>
                        <span>Status</span>
                      </th>
                      <th>
                        <span>Date</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.map((activity, index) => (
                      <tr key={index}>
                        <td>{activity.email}</td>
                        <td>
                          <span className="plan-badge">{activity.plan}</span>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${activity.status.toLowerCase()}`}
                          >
                            {activity.status}
                          </span>
                        </td>
                        <td className="activity-date">{activity.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="alerts-sidebar">
              <div className="alerts-card">
                <div className="alerts-card-header">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <h3>Alerts</h3>
                </div>
                <div className="alerts-list">
                  <div className="alert-item failed">
                    <div className="alert-count">
                      <span>3</span>
                    </div>
                    <div className="alert-content">
                      <p className="alert-title">Failed Payments</p>
                      <p className="alert-description">
                        Require immediate attention
                      </p>
                    </div>
                  </div>

                  <div className="alert-item cancelled">
                    <div className="alert-count">
                      <span>2</span>
                    </div>
                    <div className="alert-content">
                      <p className="alert-title">Cancellations</p>
                      <p className="alert-description">
                        Subscriptions cancelled today
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="upgrade-metric-card">
                <div className="upgrade-metric-header">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <h3>Upgrade Metric</h3>
                </div>
                <div className="upgrade-metric-content">
                  <div className="upgrade-metric-main">
                    <p>Free → Paid</p>
                    <p className="upgrade-metric-value">41</p>
                    <p>conversions this month</p>
                  </div>
                  <div className="upgrade-metric-footer">
                    <p>
                      Avg. time to upgrade: <span>14 days</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </main>
    </div>
  );
}
